import { useEffect, useState } from 'react';
import api from '../services/api';

// ── Global settings store (module-level cache) ────────────────
let cachedSettings = null;
const listeners = new Set();

export const getSettings = () => cachedSettings;

export const notifyListeners = () => {
  listeners.forEach(fn => fn(cachedSettings));
};

// ── Apply settings to DOM ─────────────────────────────────────
export const applyTheme = (settings = {}) => {
  if (!settings) return;
  const root = document.documentElement;

  // CSS Variables — these affect var(--header-color) etc in CSS
  const colorMap = {
    '--header-color':  settings.header_color  || '#1a365d',
    '--footer-color':  settings.footer_color  || '#2d3748',
    '--primary-color': settings.primary_color || '#2b6cb0',
    '--bg-color':      settings.bg_color      || '#f7fafc',
  };
  Object.entries(colorMap).forEach(([k, v]) => root.style.setProperty(k, v));

  // Body background
  if (settings.bg_color) {
    document.body.style.backgroundColor = settings.bg_color;
  }

  // Page title
  if (settings.app_name) {
    document.title = settings.app_name;
  }

  cachedSettings = settings;
  notifyListeners();
};

// ── Load settings from API ────────────────────────────────────
export const loadSettings = async () => {
  try {
    const r = await api.get('/settings/public');
    if (r?.settings) {
      applyTheme(r.settings);
      return r.settings;
    }
  } catch (e) {
    // silently fail
  }
  return null;
};

// ── Hook: load on mount, re-apply on settings change ─────────
export const useAppSettings = () => {
  const [settings, setSettings] = useState(cachedSettings);

  useEffect(() => {
    // Add listener
    const listener = (s) => setSettings({ ...s });
    listeners.add(listener);

    // Load fresh from API
    loadSettings().then(s => { if (s) setSettings(s); });

    return () => listeners.delete(listener);
  }, []);

  return settings;
};

export default useAppSettings;